import * as React from 'react'
import Collection from './collection/Collection'
import Pagination from './Pagination'
import FiltersNav from './FiltersNav'
import { MessageBlock, MessageTypes, Spinner } from 'DVWebNodeUI'
import * as styles from './Content.module.scss'
import * as cn from 'classnames'
import ATFMarker from './metric/ATFMarker'
import CFMarker from './metric/CFMarker'
import AsyncCallsMarkers from './metric/AsyncCallsMarkers'
import VideoWizard from './video-wizard/VideoWizard'
import { StorefrontState, StorefrontStateCollection } from '../containers/Storefront'
import { StickyBlock } from './StickyBlock'

interface ContentProps extends StorefrontState {
  isPhablet: boolean
  loadFilteredContent: Function
  hasExperimentalFilters?: boolean
  isLoading?: boolean
  fetch: Function
  enrichCollection?: Function
  enrichSingleItem?: Function
  canLazyLoad?: boolean
  removeItem?: Function
  updateChannelItem?: Function
  loadMoreContent?: Function
  asyncCallsScopes?: string[]
  storefrontReloadPageHandler?: Function
}

const collectionHasAnyHover = (collection: StorefrontStateCollection) => collection.items.some((item) =>
  item.hoverInfo && item.hoverInfo.canHover)

interface ContentState {
  truncateCollectionList: boolean
  vwEnabled: boolean
  isFrozen: boolean
  storefrontScrollingDisabled: boolean
  filterOn: boolean
}

export interface CollectionSliceProps extends ContentProps {
  offset: number
  length?: number
  requestFocusForHover?: () => void
  collectionSliceHasFocusForHover?: boolean
  filterOn?: boolean
}

interface CollectionSliceState {
  indexOfFocus: number
}

class CollectionSlice extends React.Component<CollectionSliceProps, CollectionSliceState>  {

  constructor(props: CollectionSliceProps) {
    super(props)
    this.state = { indexOfFocus: -1 }
  }

  setFocusedCarousel(carousel: StorefrontStateCollection, index: number) {
    if (!(this.props.featureSwitches &&
      this.props.featureSwitches.hover2019 &&
      collectionHasAnyHover(carousel)))
      return
    this.setState({ indexOfFocus: index })
    this.props.requestFocusForHover && this.props.requestFocusForHover()
  }

  resetFocusedCarousel() {
    this.setState({ indexOfFocus: -1 })
  }

  render() {
    const props = this.props
    if (!props.collections) {
      return null
    }
    const endPosition = props.length !== undefined ? props.offset + props.length : props.collections.length
    const slice = props.collections.slice(props.offset, endPosition)
    return <>
      {slice.map((collection, k) => (
        <Collection
          // Default to use the index if there's no webUid for the collection
          key={collection.webUid || (collection.seeMoreLink && collection.seeMoreLink.url) 
            || collection.facetText || k}
          {...collection}
          isPhablet={props.isPhablet}
          fetch={props.fetch}
          hoverClass={props.hoverClass}
          featureSwitches={props.featureSwitches}
          isLoading={props.isLoading}
          isFrozen={props.isFrozen}
          collectionKey={props.offset + k}
          enrichCollection={props.enrichCollection}
          enrichSingleItem={props.enrichSingleItem}
          canLazyLoad={props.canLazyLoad}
          removeItem={props.removeItem}
          showBlueBars={props.showBlueBars}
          updateChannelItem={props.updateChannelItem}
          canLivePlayback={props.canLivePlayback}
          formFactor={props.formFactor}
          collectionHasFocusForHover={this.props.collectionSliceHasFocusForHover &&
            this.state.indexOfFocus === k}
          requestFocusForHover={() => this.setFocusedCarousel(collection, k)}
          resetFocusForHover={() => this.resetFocusedCarousel()}
          filterOn={this.props.filterOn}
        />
      ))}
    </>
  }
}

interface BelowFiltersNavProps extends ContentProps {
  isSplit: boolean
  length?: number
  filterOn?: boolean
}

interface BelowFiltersNavState {
  indexOfFocus: number
}


class BelowFiltersNav extends React.Component<BelowFiltersNavProps, BelowFiltersNavState>  {
  constructor(props: BelowFiltersNavProps) {
    super(props)
    this.state = { indexOfFocus: -1 }
  }

  setFocusedSlice(index: number) {
    if (!(this.props.featureSwitches && this.props.featureSwitches.hover2019))
      return
    this.setState({ indexOfFocus: index })
  }

  render() {
    const props = this.props
    const atfPosition = 3
    const superheroLength = props.isSplit ? 0 : 1
    return (
      <div>
        <CollectionSlice
          {...props}
          offset={0}
          length={superheroLength}
          requestFocusForHover={() => this.setFocusedSlice(0)}
          collectionSliceHasFocusForHover={this.state.indexOfFocus === 0}
        />
        <div className={styles.belowHero}>
          {
            (!props.isLoading && props.hasHalloweenTheme) &&
            <div className={cn(styles.halloweenBelowHero,
              { [styles.mobileHalloweenBelowHero]: props.isPhablet })} />
          }
          <div>
            {props.isLoading &&
              <div className={cn(styles.BelowFiltersNavSpinner, 'tst-filtersnav-loader')}>
                <Spinner />
              </div>}
            <CollectionSlice {...props}
              offset={1}
              length={atfPosition}
              requestFocusForHover={() => this.setFocusedSlice(1)}
              collectionSliceHasFocusForHover={this.state.indexOfFocus === 1} />
            <ATFMarker />
            <CollectionSlice {...props}
              offset={1 + atfPosition}
              length={props.length ? props.length - (atfPosition - superheroLength) : undefined}
              requestFocusForHover={() => this.setFocusedSlice(2)}
              collectionSliceHasFocusForHover={this.state.indexOfFocus === 2} />
            <Pagination {...props.pagination}
              loadMoreContent={props.loadMoreContent}
            />
            <CFMarker />
            <AsyncCallsMarkers scopes={props.asyncCallsScopes} />
          </div>
        </div>
      </div>
    )
  }
}

class Content extends React.Component<ContentProps, ContentState> {
  constructor(props: ContentProps) {
    super(props)

    // TODO(PVWebXPL-116): Add local storage flag here
    const vwEnabled = Boolean(this.props.featureSwitches && this.props.featureSwitches.vwEnabled)

    this.state = {
      truncateCollectionList: Boolean(props.featureSwitches &&
        props.featureSwitches.delayCarouselRendering),
      vwEnabled: vwEnabled,
      isFrozen: vwEnabled,
      storefrontScrollingDisabled: false,
      filterOn: false
    }
  }

  loadMoreWithTruncation() {
    if (this.state.truncateCollectionList) {
      this.setState({ truncateCollectionList: false })
    } else {
      this.props.loadMoreContent && this.props.loadMoreContent()
    }
  }

  unfreeze() {
    this.setState({
      isFrozen: false,
      storefrontScrollingDisabled: false
    })
  }

  disableStorefrontScrolling() {
    this.setState({ storefrontScrollingDisabled: true })
  }

  render() {
    const hasSuperheroInFirstSlot = this.props.collections &&
      this.props.collections.length > 0 &&
      this.props.collections[0] &&
      this.props.collections[0].collectionType === 'SuperHero'
    const hasFilterNav = this.props.facetedNav &&
      this.props.facetedNav.filter &&
      this.props.facetedNav.filter.values
    const showMessage = Boolean(!this.props.hasFailed && this.props.customerMessage)
    const isSplit = Boolean(hasSuperheroInFirstSlot && (hasFilterNav || Boolean(this.props.epgIngress || Boolean(this.props.filterToggle))))
    const padEpgButton = this.props.featureSwitches && this.props.featureSwitches.epgIngressHasExtraPadding
    const hasHalloweenTheme = this.props.featureSwitches &&
      this.props.featureSwitches.halloweenTheme

    return (
      <div className={this.state.vwEnabled && this.state.storefrontScrollingDisabled ? styles.StorefrontScrollingDisabled : ''}>
        {this.state.vwEnabled && <VideoWizard
          context={this.props.context}
          onComplete={() => this.unfreeze()}
          storefrontReloadPageHandler={this.props.storefrontReloadPageHandler}
          logger={this.props.logger}
          onShow={() => this.disableStorefrontScrolling()}
        />}
        {showMessage && <MessageBlock type={MessageTypes.warning} heading={null} >
          {this.props.customerMessage}
        </MessageBlock>}
        {isSplit && <CollectionSlice
          {...this.props}
          offset={0}
          length={1}
          isPhablet={this.props.isPhablet}
          isFrozen={this.state.isFrozen}
          filterOn={this.state.filterOn}
        />}
        {!global.__NATIVEAPP__ && <FiltersNav
          {...this.props.facetedNav}
          loadFilteredContent={this.props.loadFilteredContent}
          hasExperimentalFilters={this.props.hasExperimentalFilters}
        />}
        {!global.__NATIVEAPP__ &&
          <StickyBlock
            epgButton={this.props.epgIngress}
            isPhablet={this.props.isPhablet}
            filter={this.props.filterToggle &&
            {
              filterToggle: () => this.setState({ filterOn: !this.state.filterOn }),
              filterOn: this.state.filterOn,
              ...this.props.filterToggle
            }
            }
            requiresExtraPadding={padEpgButton} />}
        <BelowFiltersNav
          {...this.props}
          filterOn={this.state.filterOn}
          isFrozen={this.state.isFrozen}
          hasHalloweenTheme={hasHalloweenTheme}
          loadMoreContent={this.loadMoreWithTruncation.bind(this)}
          isSplit={isSplit}
          length={this.state.truncateCollectionList ? 6 : undefined} />
      </div>
    )
  }
}

export default Content
